// Service Worker Registration / Unregistration
//
// IMPORTANT: We currently ship a "killswitch" service-worker.js that
// unregisters itself and purges all caches when activated. Re-registering
// it on every page load created a reload loop (register → activate →
// unregister → controllerchange → window.location.reload → loop). This
// module no longer registers any service worker. It only performs a
// one-time cleanup per browser session of any leftover registrations and
// caches from older builds.
//
// If a real, production-grade caching service worker is reintroduced
// later, replace `register()` with a proper installation flow.

const CLEANUP_FLAG = 'sw-cleanup-done';

async function purgeBrowserCaches() {
  if (typeof window === 'undefined' || !('caches' in window)) return;
  try {
    const keys = await window.caches.keys();
    await Promise.all(keys.map((k) => window.caches.delete(k)));
  } catch {
    /* ignore */
  }
}

async function unregisterAll() {
  if (!('serviceWorker' in navigator)) return false;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    if (!registrations || registrations.length === 0) return false;
    await Promise.all(registrations.map((r) => r.unregister().catch(() => null)));
    return true;
  } catch {
    return false;
  }
}

export function register() {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  // Only attempt cleanup once per browser session. Without this guard, any
  // event that fires `controllerchange` (e.g. the killswitch SW unregistering
  // itself) would trigger another reload and loop forever.
  let alreadyDone = false;
  try {
    alreadyDone = sessionStorage.getItem(CLEANUP_FLAG) === '1';
  } catch {
    /* sessionStorage may be unavailable (private mode, ITP, etc.) */
  }
  if (alreadyDone) return;

  window.addEventListener('load', async () => {
    try {
      const had = await unregisterAll();
      await purgeBrowserCaches();
      try {
        sessionStorage.setItem(CLEANUP_FLAG, '1');
      } catch {
        /* ignore */
      }
      // Only reload if we actually evicted a controlling SW, so the user
      // lands on fresh, un-cached resources. The session flag above
      // prevents this from ever firing twice.
      if (had) {
        window.location.reload();
      }
    } catch {
      /* never block navigation */
    }
  });
}

export function applyUpdateNow() {
  // No SW is registered, so an update simply means reloading the page.
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
}

export function unregister() {
  unregisterAll();
  purgeBrowserCaches();
}

export function isInstalled() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  if (window.navigator.standalone === true) return true;
  return false;
}

export function canInstall() {
  if (typeof window === 'undefined') return false;
  return (
    'BeforeInstallPromptEvent' in window ||
    ('serviceWorker' in navigator && !isInstalled())
  );
}
